import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import "./CompanyAdminTaskVerification.css";
import "../../../Admin/Task Verification/TaskVerification.css";
import {
  TASK_VERIFICATION_PAGE_SIZE,
  useGetPendingCompanyAdminTaskQuery,
} from "../../../../redux-toolkit/features/books/bookApiSlice";
import { useInfiniteScroll } from "../../../../hooks/useInfiniteScroll";
import { useGetAdminTimeTrialSubmissionsQuery } from "../../../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import { Tasks } from "../../../Admin/Task Verification/Tasks";
import { TimeTrialGroupCard } from "../../../Admin/Task Verification/TimeTrialGroupCard";
import {
  ORG_TASK_SORT_OPTIONS,
  TaskVerificationToolbar,
} from "../../../Admin/Task Verification/TaskVerificationToolbar";
import ProgressBar from "../../../UI/ProgressBar";
import { NoRecord } from "../../../UI/No record/NoRecord";

const SUPER_ADMIN_TASK_VERIFICATION_PATH =
  "/super-admin/manage-organization/task-verification";
const TIME_TRIALS_TAB = "time-trials";

const DEFAULT_ORG_SORT = ORG_TASK_SORT_OPTIONS[0];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: "20px 0px" }}>{children}</Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export const CompanyAdminTaskVerification = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get("tab") === TIME_TRIALS_TAB ? 1 : 0;
  const [page, setPage] = useState(1);
  const activeTaskType = "ORG";

  const [orgSearchInput, setOrgSearchInput] = useState("");
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSort, setOrgSort] = useState(DEFAULT_ORG_SORT);

  const [timeTrialSearchInput, setTimeTrialSearchInput] = useState("");
  const [timeTrialSearchQuery, setTimeTrialSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setOrgSearchQuery(orgSearchInput.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [orgSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeTrialSearchQuery(timeTrialSearchInput.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [timeTrialSearchInput]);

  const {
    data: taskData,
    isLoading: taskLoading,
    isFetching: taskFetching,
    isError: taskError,
    refetch: refetchTasks,
  } = useGetPendingCompanyAdminTaskQuery(
    {
      type: activeTaskType,
      page,
      limit: TASK_VERIFICATION_PAGE_SIZE,
      search: orgSearchQuery,
      sortBy: orgSort.sortBy,
      sortOrder: orgSort.sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const pagination = taskData?.data?.pagination;
  const hasMore = pagination ? page < pagination.totalPages : false;

  const loadMore = useCallback(() => {
    if (!taskFetching && hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [taskFetching, hasMore]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: taskFetching,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    setPage(1);
  }, [activeTaskType, orgSearchQuery, orgSort.sortBy, orgSort.sortOrder]);

  const {
    data: timeTrialData,
    isLoading: timeTrialLoading,
    isFetching: timeTrialFetching,
    isError: timeTrialError,
    refetch: refetchTimeTrials,
  } = useGetAdminTimeTrialSubmissionsQuery(
    {
      search: timeTrialSearchQuery,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const handleChange = (_event, newValue) => {
    if (newValue === 1) {
      setSearchParams({ tab: TIME_TRIALS_TAB });
    } else {
      setSearchParams({});
    }
  };

  const timeTrialGroups = timeTrialData?.data || [];

  const showTaskLoading =
    (taskLoading || (taskFetching && !taskData)) && value === 0 && page === 1;
  const showTaskLoadMore = taskFetching && value === 0 && page > 1;
  const showTimeTrialLoading = (timeTrialLoading || timeTrialFetching) && value === 1;

  return (
    <div className="container">
      <div className="taskHod__layout">
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="task verification tabs"
            >
              <Tab
                label="Organizational"
                sx={{
                  textTransform: "capitalize",
                  marginRight: "20px",
                  fontWeight: "500",
                }}
                {...a11yProps(0)}
              />
              <Tab
                label="Time Trials"
                sx={{
                  textTransform: "capitalize",
                  fontWeight: "500",
                }}
                {...a11yProps(1)}
              />
            </Tabs>

            {value === 0 ? (
              <TaskVerificationToolbar
                searchVal={orgSearchInput}
                setSearchVal={setOrgSearchInput}
                sortValue={orgSort}
                onSortChange={setOrgSort}
                sortOptions={ORG_TASK_SORT_OPTIONS}
              />
            ) : (
              <TaskVerificationToolbar
                searchVal={timeTrialSearchInput}
                setSearchVal={setTimeTrialSearchInput}
                placeholder="Search time trials"
                showSort={false}
              />
            )}
          </Box>

          <TabPanel value={value} index={0}>
            {showTaskLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <ProgressBar />
              </Box>
            ) : taskError ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Unable to load task verification
                </Typography>
                <button type="button" onClick={() => refetchTasks()}>
                  Try again
                </button>
              </div>
            ) : (taskData?.data?.taskCompletionRequests?.length ?? 0) > 0 ? (
              <>
                <div className="taskHod">
                  {(taskData?.data?.taskCompletionRequests ?? []).map((curElm, index) => (
                    <Tasks
                      key={curElm?.user?._id || index}
                      allTask={curElm}
                      taskType={activeTaskType}
                      listBasePath={SUPER_ADMIN_TASK_VERIFICATION_PATH}
                    />
                  ))}
                </div>
                <div ref={sentinelRef} />
                {showTaskLoadMore && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <ProgressBar />
                  </Box>
                )}
              </>
            ) : (
              <NoRecord />
            )}
          </TabPanel>

          <TabPanel value={value} index={1}>
            {showTimeTrialLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <ProgressBar />
              </Box>
            ) : timeTrialError ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Unable to load time trial submissions
                </Typography>
                <button type="button" onClick={() => refetchTimeTrials()}>
                  Try again
                </button>
              </div>
            ) : timeTrialGroups.length > 0 ? (
              <div className="taskHod">
                {timeTrialGroups.map((group) => (
                  <TimeTrialGroupCard
                    key={group?.timetrial?._id}
                    group={group}
                    listBasePath={SUPER_ADMIN_TASK_VERIFICATION_PATH}
                  />
                ))}
              </div>
            ) : (
              <NoRecord />
            )}
          </TabPanel>
        </Box>
      </div>
    </div>
  );
};
